import { FC, ReactNode, useEffect } from "react";
import { FormSelectGroupOption, FormSelectValue } from "../../../types/Form";

const Select: FC<{
  value: FormSelectValue;
  defaultValue?: FormSelectValue;
  optionsGroups: FormSelectGroupOption[];
  onChange: (value: FormSelectValue) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  classNameForSelect?: string;
  isIncorrectValue?: boolean;
  disabled?: boolean;
  name: string;
  loading?: boolean;
  labelWidth?: string;
  selectWidth?: string;
  label?: string | ReactNode;
  multiple?: boolean;
  children?: ReactNode;
  color?: "gray" | "white";
  withNullbaleValue?: boolean;
}> = ({
  value,
  optionsGroups,
  onChange,
  onFocus,
  onBlur,
  className,
  classNameForSelect,
  isIncorrectValue,
  disabled,
  name,
  loading,
  labelWidth,
  label,
  defaultValue,
  multiple,
  selectWidth,
  children,
  color = "gray",
  withNullbaleValue,
}) => {
  const loadingValue = "Loading...";
  const noDataValue = "No data";

  const options = optionsGroups.flatMap((group) => group.options);
  const optionValue = (option: typeof options[number]) =>
    typeof option === "object" ? option.id : option;
  const hasDefault = options.some((option) =>
    String(optionValue(option)) === String(defaultValue) &&
    !(typeof option === "object" && option.disabled)
  );
  const shouldDefault = value === undefined && defaultValue !== undefined &&
    hasDefault && !loading && !disabled && !withNullbaleValue;
  const selectedValue = shouldDefault ? defaultValue : value;
  const normalizedValue = selectedValue == null ||
    (typeof selectedValue === "number" && Number.isNaN(selectedValue))
    ? "" : selectedValue;
  const hasSelectedOption = options.some((option) =>
    String(optionValue(option)) === String(normalizedValue)
  );
  const currentValue = loading ? loadingValue : normalizedValue;
  // Presentation metadata follows the resolved label, never a database-specific ID.
  const selectedOption = options.find((option) =>
    String(optionValue(option)) === String(normalizedValue)
  );
  const selectedLabel = loading || !hasSelectedOption ? undefined :
    typeof selectedOption === "object" ? selectedOption.value : selectedOption;

  useEffect(() => {
    if (shouldDefault) onChange(defaultValue);
  }, [shouldDefault, defaultValue, onChange]);

  return (
    <label
      className={`select-box ${className || ""} ${
        label ? "select-box_with-label" : ""
      }`}
      htmlFor={name}
    >
      {label && (
        <div
          className="select-box__label"
          style={{ width: `${labelWidth || "max-content"}` }}
        >
          {label}
          {isIncorrectValue && <i className="icon-cancel-circled red" />}
        </div>
      )}
      <div className="select-box__select-wrapper">
        {children && children}
        <select
          name={name}
          id={name}
          data-selected-label={selectedLabel}
          multiple={multiple}
          className={`select-box__select select-box__select_${color} ${
            classNameForSelect || ""
          } ${multiple ? "select-box__select_multiple" : ""} ${
            isIncorrectValue ? "select-box__input_error" : ""
          }`}
          style={{
            maxWidth: `${selectWidth || (multiple ? "150px" : "unset")}`,
          }}
          disabled={disabled || loading}
          value={
            multiple && currentValue !== undefined
              ? [currentValue.toString()]
              : currentValue
          }
          onChange={(event) => {
            const rawValue = event.currentTarget.value;
            const option = options.find((item) => String(optionValue(item)) === rawValue);
            // Preserve the option ID's type, including numeric zero and blank values.
            onChange(option === undefined ? rawValue : optionValue(option));
          }}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {loading && <option disabled>{loadingValue}</option>}
          {!loading && !hasSelectedOption && (
            <option value={normalizedValue} disabled={!withNullbaleValue || normalizedValue !== ""}>
              {normalizedValue !== "" ? `Unavailable (${normalizedValue})` : options.length ? "" : noDataValue}
            </option>
          )}
          {!loading && withNullbaleValue && normalizedValue !== "" &&
            !options.some((option) => optionValue(option) === "") && <option value="" />}
          {!loading &&
            optionsGroups.length &&
            optionsGroups.map((optionsGroup, indexOptionsGroup) => {
              if (optionsGroups.length > 1) {
                return (
                  <optgroup
                    label={optionsGroup?.label || ""}
                    key={`select-option-group-${name}-${indexOptionsGroup}`}
                  >
                    {optionsGroup.options.map((selectOption, index) => {
                      return (
                        <option
                          key={`select-option-item-${name}-${indexOptionsGroup}-${index}`}
                          value={
                            typeof selectOption === "object"
                              ? selectOption?.id
                              : selectOption
                          }
                          disabled={
                            typeof selectOption === "object"
                              ? selectOption?.disabled
                              : undefined
                          }
                        >
                          {typeof selectOption === "object"
                            ? selectOption?.value
                            : selectOption}
                        </option>
                      );
                    })}
                  </optgroup>
                );
              } else {
                return optionsGroup.options.map((selectOption, index) => {
                  return (
                    <option
                      key={`select-option-item-${name}-${indexOptionsGroup}-${index}`}
                      value={
                        typeof selectOption === "object"
                          ? selectOption?.id
                          : selectOption
                      }
                      disabled={
                        typeof selectOption === "object"
                          ? selectOption?.disabled
                          : undefined
                      }
                    >
                      {typeof selectOption === "object"
                        ? selectOption?.value
                        : selectOption}
                    </option>
                  );
                });
              }
            })}
        </select>
      </div>
    </label>
  );
};

export default Select;
